import type { components } from "@majitap/sdk";
import { Button, formatDateTime, formatPhone, PageError, PageHeader, PageLoading } from "@majitap/ui";
import { Alert } from "@majitap/ui/arc/alert/alert";
import { Badge } from "@majitap/ui/arc/badge/badge";
import { MetricCard } from "@majitap/ui/arc/metric-card/metric-card";
import { Select } from "@majitap/ui/arc/select/select";
import { SortableDataTable } from "@majitap/ui/arc/sortable-data-table/sortable-data-table";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { type ReactNode, useState } from "react";
import { api, errorOf, ok } from "../../api";
import { useCompanyFormat } from "../../company";
import { messageFor } from "../../error-copy";
import { filterSearch } from "../../list-search";
import { ConfirmDialog } from "../shared/confirm";
import styles from "./settings.module.css";

type Readiness = components["schemas"]["Readiness"];
type Reminded = components["schemas"]["Reminded"];
type Audience = components["schemas"]["ReadinessAudience"];

const WINDOWS = ["30", "60", "90", "180"] as const;
export const readinessFilters = filterSearch({ days: WINDOWS });

const AUDIENCE: Record<Audience, { label: string; how: string }> = {
  drivers: { label: "Drivers", how: "an SMS to set a new PIN in the driver app" },
  customers: { label: "Customers", how: "an SMS to set a new PIN in the customer app" },
  staff: { label: "Staff", how: "their invitation email again, with a link to set a password" },
};

/** One audience's card: the share ready, how many of how many, and the target for drivers. */
export function readinessCard(audience: Audience, r: Readiness) {
  const group = r[audience];
  const target = audience === "drivers" ? r.drivers.meets_target : null;
  return {
    label: `${AUDIENCE[audience].label} ready`,
    value: group.ready_pct ?? 0,
    context:
      group.active === 0
        ? "Nobody to sign in"
        : `${group.signed_in} of ${group.active} signed in · ${group.with_secret} set a ${audience === "staff" ? "password" : "PIN"}`,
    change: target === null ? undefined : target ? `Target ${r.target_pct}% met` : `Target ${r.target_pct}%`,
  };
}

/** What a round of reminders did: how many were sent, and who can't be reached that way. */
export function RemindResult({ result }: { result: Reminded }) {
  const who = AUDIENCE[result.audience].label.toLowerCase();
  return (
    <>
      <Alert tone={result.unreachable.length ? "warning" : "success"} title={`Reminded ${result.sent_to} ${who}`}>
        {result.unreachable.length
          ? `${result.unreachable.length} can't be reached: no phone number${result.audience === "staff" ? " or a real email" : ""}. Reach them another way.`
          : "Everyone not ready yet was reached."}
      </Alert>
      {result.unreachable.length ? (
        <ul className={styles.list} aria-label="Can't be reached">
          {result.unreachable.map((u) => (
            <li key={u.user_id}>{u.name}</li>
          ))}
        </ul>
      ) : null}
    </>
  );
}

/**
 * Sign-in readiness (P8-25): after the cut-over nobody has a PIN or password in the new system.
 * Who has signed in, the drivers still to do it (soonest delivery first), and reminders.
 */
export function ReadinessPage(): ReactNode {
  const search = useSearch({ from: "/console/settings/readiness" });
  const navigate = useNavigate({ from: "/settings/readiness" });
  const format = useCompanyFormat();
  const queryClient = useQueryClient();
  // number-ok: a count of days, not money
  const days = Number(search.days ?? "90");
  const summary = useQuery({
    queryKey: ["readiness", days],
    queryFn: () => ok(api.GET("/v1/admin/readiness", { params: { query: { active_within_days: days } } })),
  });
  const drivers = useQuery({
    queryKey: ["readiness", "drivers"],
    queryFn: async () => (await ok(api.GET("/v1/admin/readiness/drivers"))).items,
  });
  const remind = useMutation({
    mutationFn: (audience: Audience) =>
      ok(api.POST("/v1/admin/readiness/reminders", { body: { audience, active_within_days: days } })),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["readiness"] }),
  });
  return (
    <>
      <PageHeader
        title="Sign-in readiness"
        description="Who has signed in to the new system since the cut-over, and reminders for who hasn't."
        actions={
          <Select
            label="Customers who ordered within"
            value={String(days)}
            onValueChange={(v) =>
              navigate({ search: { days: v === "90" ? undefined : (v as (typeof WINDOWS)[number]) } })
            }
            options={WINDOWS.map((d) => ({ value: d, label: `${d} days` }))}
          />
        }
      />
      {summary.status === "pending" ? (
        <PageLoading label="Loading readiness" />
      ) : summary.status === "error" ? (
        <PageError message={messageFor(errorOf(summary.error))} onRetry={() => summary.refetch()} />
      ) : (
        <div className={styles.figures}>
          {(["drivers", "customers", "staff"] as const).map((a) => {
            const card = readinessCard(a, summary.data);
            return (
              <div key={a} className={styles.stack}>
                <MetricCard
                  label={card.label}
                  value={card.value}
                  suffix="%"
                  context={card.context}
                  change={card.change}
                />
                <RemindButton audience={a} busy={remind.isPending} onRemind={() => remind.mutateAsync(a)} />
              </div>
            );
          })}
        </div>
      )}
      {remind.data ? <RemindResult result={remind.data} /> : null}
      {remind.error ? (
        <Alert tone="danger" title="Not sent">
          {messageFor(errorOf(remind.error))}
        </Alert>
      ) : null}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Drivers still to sign in</h2>
        <p className={styles.muted}>
          Working drivers who haven't signed in to the new driver app. Soonest delivery first.
        </p>
        {drivers.status === "error" ? (
          <PageError message={messageFor(errorOf(drivers.error))} onRetry={() => drivers.refetch()} />
        ) : drivers.status === "pending" ? (
          <PageLoading label="Loading drivers" />
        ) : (
          <SortableDataTable
            caption="Drivers still to sign in"
            rowKey="driver_id"
            rows={drivers.data}
            itemName={{ one: "driver", other: "drivers" }}
            columns={[
              {
                key: "name",
                label: "Driver",
                sortable: false,
                render: (_v, d) => (
                  <span className={styles.stack}>
                    <span>{d.name}</span>
                    <span className={styles.muted}>
                      {[formatPhone(d.phone), d.partner].filter(Boolean).join(" · ")}
                    </span>
                  </span>
                ),
              },
              {
                key: "has_secret",
                label: "PIN",
                sortable: false,
                render: (_v, d) =>
                  d.has_secret ? <Badge tone="info">Set, not signed in</Badge> : <Badge tone="warning">Not set</Badge>,
              },
              {
                key: "next_delivery",
                label: "Next delivery",
                sortable: false,
                render: (_v, d) =>
                  d.next_delivery ? (
                    <span className={styles.stack}>
                      <span className={styles.code}>{d.next_delivery.delivery_number}</span>
                      <span className={styles.muted}>
                        {d.next_delivery.scheduled_at
                          ? formatDateTime(d.next_delivery.scheduled_at, format)
                          : "Not scheduled"}
                      </span>
                    </span>
                  ) : (
                    <span className={styles.muted}>None</span>
                  ),
              },
              {
                key: "last_reminded_at",
                label: "Last reminded",
                sortable: false,
                render: (_v, d) => (
                  <span className={styles.muted}>
                    {d.last_reminded_at ? formatDateTime(d.last_reminded_at, format) : "Never"}
                  </span>
                ),
              },
            ]}
          />
        )}
      </section>
    </>
  );
}

function RemindButton({
  audience,
  busy,
  onRemind,
}: {
  audience: Audience;
  busy: boolean;
  onRemind: () => Promise<unknown>;
}) {
  const [asking, setAsking] = useState(false);
  return (
    <>
      <Button variant="secondary" size="sm" disabled={busy} onClick={() => setAsking(true)}>
        Remind {AUDIENCE[audience].label.toLowerCase()}
      </Button>
      {asking ? (
        <ConfirmDialog
          title={`Remind ${AUDIENCE[audience].label.toLowerCase()} not ready?`}
          description={`Everyone not signed in yet gets ${AUDIENCE[audience].how}. People already ready get nothing.`}
          action="Send reminders"
          run={onRemind}
          onClose={() => setAsking(false)}
        />
      ) : null}
    </>
  );
}
